/**
 * Background (W2-01a preview-gl; rendering.md R§1.3, R§8 steps 1-2, ARCHITECTURE.md §7.3 step 1).
 *
 *  1. clear the whole target to black;
 *  2. the blurred illustration (Phira's 3-pass box blur, computed by the io worker) crop-centre fitted over the
 *     WHOLE window, not just the viewport (scaled to cover, centred, overflow cropped); black when missing or
 *     still loading;
 *  3. black at alpha 0.3 over the whole window;
 *  4. inside the chart viewport only, black at alpha res.alpha * backgroundDim.
 * Net brightness inside the viewport: x 0.7 x (1 - dim); in the bars: x 0.7.
 */

import { colorByte } from '../gl/quadBatch';
import { beginQuads, type PassContext } from './passContext';

/** Whole-window darkening over the illustration (R§1.3 step 3). */
export const BACKGROUND_SHADE = 0.3;

/** UV rectangle [u0, v0, u1, v1] that crop-centre fits an `iw` x `ih` image into a `w` x `h` box. */
export function coverUv(iw: number, ih: number, w: number, h: number): readonly [number, number, number, number] {
  if (!(iw > 0 && ih > 0 && w > 0 && h > 0)) return [0, 0, 1, 1];
  const scale = Math.max(w / iw, h / ih);
  const vw = w / scale / iw;
  const vh = h / scale / ih;
  const u0 = (1 - vw) / 2;
  const v0 = (1 - vh) / 2;
  return [u0, v0, u0 + vw, v0 + vh];
}

export function drawBackground(ctx: PassContext): void {
  const { list, vp, state, batch } = ctx;
  batch.flush();
  state.setScissor(null);
  state.clear(0, 0, 0, 1);
  const winW = vp.winW;
  const winH = vp.winH;
  const ill = list.background.illustration;
  // Null while the io worker blurs it (the provider's onReady triggers the redraw): black meanwhile, like Phira.
  const bitmap = ill !== null && ctx.provider !== null ? ctx.provider.blurredIllustration(ill) : null;
  if (bitmap !== null) {
    const tex = ctx.textures.get(bitmap);
    if (tex !== null) {
      const [u0, v0, u1, v1] = coverUv(bitmap.width, bitmap.height, winW, winH);
      beginQuads(ctx, ctx.pxToClip, tex);
      // Image row 0 (top) is texture v 0: the top of the window samples v0.
      batch.rect([1, 0, 0, 1, 0, 0], 0, 0, winW, winH, u0, v0, u1, v1, 255, 255, 255, 255);
    }
  }
  beginQuads(ctx, ctx.pxToClip, null);
  batch.rect([1, 0, 0, 1, 0, 0], 0, 0, winW, winH, 0, 0, 1, 1, 0, 0, 0, colorByte(BACKGROUND_SHADE));
  const dim = list.resAlpha * list.background.dim;
  const a = colorByte(dim);
  if (a > 0) batch.rect([1, 0, 0, 1, 0, 0], vp.vx, vp.vy, vp.vx + vp.W, vp.vy + vp.H, 0, 0, 1, 1, 0, 0, 0, a);
}
